/**
 * Responsibility: turn biome weights and a normalised elevation into a line colour.
 *
 * Deliberately free of Three.js: colours come out as plain `{ r, g, b }` objects with
 * components in 0..1, which the render layer wraps into whatever it needs. That keeps the
 * colour maths unit testable.
 *
 * ## How colour is chosen
 *
 * Two steps, in this order:
 *   1. Each biome contributes its own low-to-high gradient, sampled at the current
 *      normalised elevation.
 *   2. Those per-biome colours are mixed by biome weight.
 *
 * Doing it in that order means a transition zone shows a genuine blend of both biomes'
 * palettes rather than snapping at the boundary.
 *
 * Interpolation happens in plain sRGB component space. Linear-light blending would be
 * more physically correct, but for two-stop neon gradients the difference is not visible
 * and sRGB keeps the numbers matching what you would pick in a colour picker.
 */

import { getBiome } from './biomes/index.js';

/** Largest 8-bit channel value, used to unpack hex colours. */
const MAX_CHANNEL = 255;

/**
 * Unpack a 0xRRGGBB integer into normalised components.
 *
 * @param {number} hex
 * @returns {{r: number, g: number, b: number}} Components in 0..1.
 */
export function hexToRgb(hex) {
  const value = hex >>> 0;
  return {
    r: ((value >> 16) & MAX_CHANNEL) / MAX_CHANNEL,
    g: ((value >> 8) & MAX_CHANNEL) / MAX_CHANNEL,
    b: (value & MAX_CHANNEL) / MAX_CHANNEL,
  };
}

/**
 * Linearly interpolate between two colours.
 *
 * @param {{r: number, g: number, b: number}} from
 * @param {{r: number, g: number, b: number}} to
 * @param {number} t - Clamped to 0..1.
 * @returns {{r: number, g: number, b: number}}
 */
export function mixRgb(from, to, t) {
  const amount = Math.min(1, Math.max(0, t));
  return {
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  };
}

/**
 * Create a palette sampler bound to a set of biomes.
 *
 * @param {Array<object>} [biomes] - Biome descriptors. Defaults to the registry via lookup.
 * @returns {{sample: (weights: Array<{id: string, weight: number}>, normalizedElevation: number) => {r: number, g: number, b: number}}}
 */
export function createPalette(biomes = null) {
  /*
    Pre-unpack every biome's gradient stops. Unpacking hex on every vertex of every tile
    would be thousands of redundant bit operations per frame.
  */
  const gradientById = new Map();

  function gradientFor(id) {
    let gradient = gradientById.get(id);
    if (!gradient) {
      const biome = biomes ? biomes.find((b) => b.id === id) : getBiome(id);
      if (!biome) {
        throw new Error(`palette: no biome registered with id "${id}"`);
      }
      gradient = {
        low: hexToRgb(biome.palette.LOW),
        high: hexToRgb(biome.palette.HIGH),
      };
      gradientById.set(id, gradient);
    }
    return gradient;
  }

  return {
    /**
     * Colour for a point, given its biome weights and normalised elevation.
     *
     * @param {Array<{id: string, weight: number}>} weights - Expected to sum to 1.
     * @param {number} normalizedElevation - 0..1, where 1 is the tallest possible terrain.
     * @returns {{r: number, g: number, b: number}} Components in 0..1.
     */
    sample(weights, normalizedElevation) {
      const elevation = Math.min(1, Math.max(0, normalizedElevation));

      let r = 0;
      let g = 0;
      let b = 0;

      for (const { id, weight } of weights) {
        if (weight <= 0) continue;

        const gradient = gradientFor(id);
        const colour = mixRgb(gradient.low, gradient.high, elevation);

        r += colour.r * weight;
        g += colour.g * weight;
        b += colour.b * weight;
      }

      /*
        Clamp defensively. A weight provider that sums slightly above 1 through floating
        point error would otherwise push a channel past 1, and an out-of-range colour
        interacts badly with the bloom threshold.
      */
      return {
        r: Math.min(1, Math.max(0, r)),
        g: Math.min(1, Math.max(0, g)),
        b: Math.min(1, Math.max(0, b)),
      };
    },
  };
}
